import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDynamicTableHeight } from '@/hooks/use-dynamic-table-height';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollableTabsContainer } from '@/components/ui/scrollable-tabs-container';
import { StickyFooter } from '@/components/ui/sticky-footer';
import { ClientCombobox } from '@/components/ui/client-combobox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Calendar,
  Coins,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  BarChart3,
  Edit,
  MoreVertical,
  UserCheck,
  UserX,
  ArrowUpDown,
  Settings2,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  LayoutDashboard,
  Landmark,
  PieChart,
  Building2,
  Layers,
  UsersRound,
  Milestone as MilestoneIcon,
  Banknote,
  GitPullRequestArrow,
  ArrowLeft,
  History,
  Download,
  ShoppingCart,
  Receipt,
} from 'lucide-react';
import { SensitiveData } from '@/components/sensitive-data';
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell as RechartsCell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { 
  BudgetCard, 
  SalesBudgetView, 
  CostBudgetView, 
  ResourceBudgetWrapper,
  BudgetSummaryCards,
  BudgetAlertsSection,
  EditBudgetModal,
  BudgetHistoryModal,
  BudgetExportModal
} from '@/components/blocks/budget';
import { EditResourceBudgetPage } from '@/components/blocks/budget/edit-resource-budget-page';
import { EditCostBudgetPage } from '@/components/blocks/budget/edit-cost-budget-page';
import { EditSalesBudgetPage } from '@/components/blocks/budget/edit-sales-budget-page';
import { ResourceBudgetMetrics } from '@/components/blocks/budget/resource-budget-metrics';
import { UnfulfilledRolesTable } from '@/components/blocks/unfulfilled-roles-table';
import { useBudgetPermissions } from '@/hooks/use-budget-permissions';
import { useBudgetData, useUpdateBudget } from '@/hooks/use-budget-data';
import { useResourceBudgetSettings } from '@/hooks/use-resource-budget-settings';
import { enrichBudgetWithAllocatedHours } from '@/lib/budget-team-utils';
import { useCompany } from '@/contexts/CompanyContext';
import type { BudgetData } from '@/types';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { toast } from 'sonner';
import { CostCentresTable, DEFAULT_COST_CENTRE_COLUMNS, type CostCentre, type CostCentreColumnDefinition } from '@/components/blocks/cost-centres/cost-centres-table';
import { CostCentresColumnConfigurator } from '@/components/blocks/cost-centres/cost-centres-column-configurator';
import { CostCentresHierarchicalTable, type HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import { PurchasesCommitmentsTab } from '@/components/blocks/purchases/purchases-commitments-tab';
import { TimeEntriesTab } from '@/components/blocks/time/time-entries-tab';
import { ExpenseEntriesTab } from '@/components/blocks/expenses/expense-entries-tab';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfigureStructureModal } from '@/components/blocks/cost-centres/configure-structure-modal';
import { CostCentreDetailsPanel } from '@/components/blocks/cost-centres/cost-centre-details-panel';
import { CreateCostCentrePanel } from '@/components/blocks/cost-centres/create-cost-centre-panel';
import { CostCentreFullModal } from '@/components/blocks/cost-centres/cost-centre-full-modal';
import { formatHours } from '@/lib/budget-utils';
import {
  fetchProjectById,
  fetchClients,
  fetchEmployees,
  fetchResourceAllocations,
  fetchProjectDetails,
  fetchCostCentresByProjectId,
  fetchTimeEntriesByProjectId,
  fetchExpensesByProjectId,
  fetchMilestonesByProjectId,
  fetchChangeRequestsByProjectId,
  createCostCentre,
  updateProject,
  getDataMode,
  createResourceAllocation,
  deleteResourceAllocation,
  createPersonBudget,
  fetchResourceBudgetIdForProject,
} from '@/lib/data-service';
import type { FetchExpensesOptions } from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateForInput,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  cn,
} from '@/lib/utils';
import { ROUTES } from '@/routes';
import type {
  Project,
  Client,
  Employee,
  TimeEntry,
  Expense,
  Milestone,
  ResourceAllocation,
  ChangeRequest,
} from '@/types';

interface ProjectDetailsPageProps {
  projectId?: string;
  onBack?: () => void;
}

interface TeamMember {
  id: number | string;
  employeeId: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  allocatedHours: number;
  actualHours: number;
  remaining: number;
  startDate: string;
  endDate: string;
  status: 'On Track' | 'At Risk' | 'Over Allocated';
  utilisationPercent: number;
}

export function ProjectDetailsPage({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  // Get projectId from URL params or props (props for backward compatibility)
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp || projectIdFromUrl || '';
  const numericProjectId = projectId ? parseInt(projectId, 10) : 0;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [recentClientIds, setRecentClientIds] = useState<string[]>([]);
  const [projectManager, setProjectManager] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [localBudgetData, setLocalBudgetData] = useState<BudgetData | null>(null);
  const [isStructureView, setIsStructureView] = useState(false);
  const [sortField, setSortField] = useState<keyof TeamMember>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();
  
  // Budget permissions hook
  const budgetPermissions = useBudgetPermissions();
  const { isBudgetingEnabled } = budgetPermissions;
  
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Project>>({});
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Collapsible KPI cards state (expanded by default)
  const [isKpiOpen, setIsKpiOpen] = useState(true);
  
  // Expense type checkboxes state - driven by company integration settings
  const [includeApprovedExpenses, setIncludeApprovedExpenses] = useState(currentCompany?.approvedExpensesEnabled ?? true);
  const [includeCreditCardExpenses, setIncludeCreditCardExpenses] = useState(currentCompany?.creditCardExpensesEnabled ?? false);
  const [expensesLoading, setExpensesLoading] = useState(false);

  // Cost Centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [hierarchicalCostCentres, setHierarchicalCostCentres] = useState<HierarchicalCostCentre[]>([]);
  const [costCentreColumns, setCostCentreColumns] = useState<CostCentreColumnDefinition[]>(DEFAULT_COST_CENTRE_COLUMNS);
  const [costCentreColumnConfigOpen, setCostCentreColumnConfigOpen] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);
  const [selectedCostCentre, setSelectedCostCentre] = useState<CostCentre | null>(null);
  const [costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen] = useState(false);
  const [costCentreFullModalOpen, setCostCentreFullModalOpen] = useState(false);
  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);

  // Budget state
  const { data: budgetData, loading: budgetLoading, error: budgetError, refetch: refetchBudget } = useBudgetData(numericProjectId);
  const { updateBudget, loading: updateBudgetLoading } = useUpdateBudget();
  const { settings: resourceSettings } = useResourceBudgetSettings();
  const [salesBudgetEditModalOpen, setSalesBudgetEditModalOpen] = useState(false);
  const [editSalesBudgetPageOpen, setEditSalesBudgetPageOpen] = useState(false);
  const [costBudgetEditModalOpen, setCostBudgetEditModalOpen] = useState(false);
  const [editCostBudgetPageOpen, setEditCostBudgetPageOpen] = useState(false);
  const [resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen] = useState(false);
  const [editResourceBudgetPageOpen, setEditResourceBudgetPageOpen] = useState(false);
  const [budgetHistoryModalOpen, setBudgetHistoryModalOpen] = useState(false);
  const [budgetExportModalOpen, setBudgetExportModalOpen] = useState(false);
  const [activeBudgetType, setActiveBudgetType] = useState<'sales' | 'cost' | 'resource'>('sales');
  
  // Check if resource budgeting is enabled
  const isResourceBudgetEnabled = resourceSettings.budgetType !== 'none' && budgetPermissions.settings.permissions.resourceBudgetEnabled;
  
  // Enrich budget data with allocated hours from team members
  const enrichedBudgetData = useMemo(() => {
    // Use local budget data if available (after assignments), otherwise use fetched data
    const dataToEnrich = localBudgetData || budgetData;
    if (!dataToEnrich || !dataToEnrich.resourceBudget) return dataToEnrich;
    
    return {
      ...dataToEnrich,
      resourceBudget: enrichBudgetWithAllocatedHours(
        dataToEnrich.resourceBudget,
        teamMembers
      ),
    };
  }, [budgetData, localBudgetData, teamMembers]);
  
  // Debug logging
  console.log('[ProjectDetails] Budget permissions:', {
    isBudgetingEnabled,
    salesEnabled: budgetPermissions.settings.permissions.salesBudgetEnabled,
    costEnabled: budgetPermissions.settings.permissions.costBudgetEnabled,
    resourceEnabled: isResourceBudgetEnabled,
  });

  // Dynamic height for tabs container
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsContainerHeight = useDynamicTableHeight(tabsContainerRef, 24); // 24px bottom padding

  // Helper functions to format display values to match dropdown labels
  const formatProjectType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'fixed-price': 'Fixed Price',
      'time-and-materials': 'Time & Materials',
      'retainer': 'Retainer',
    };
    return typeMap[type] || type;
  };

  const formatRevenueRecognition = (method: string): string => {
    const methodMap: Record<string, string> = {
      'percentage-of-completion': 'Percentage of Completion',
      'completed-contract': 'Completed Contract',
      'cash-basis': 'Cash Basis',
    };
    return methodMap[method] || method;
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    console.log('Edit mode activated for project:', projectId);
    if (project) {
      setEditFormData({
        name: project.name,
        description: project.description,
        status: project.status,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
      });
      setIsEditMode(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      setIsEditMode(false);
      setEditFormData({});
    }
  };

  const handleConfirmCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    toast.info('Changes discarded');
  };

  const handleSaveChanges = async () => {
    try {
      if (!project) return;
      
      const dataMode = getDataMode();
      
      if (dataMode === 'live') {
        const updateData = {
          id: numericProjectId,
          name: editFormData.name ?? project.name,
          description: editFormData.description ?? project.description,
          status: editFormData.status ?? project.status,
          type: editFormData.type ?? project.type,
          revenueRecognitionMethod: editFormData.revenueRecognitionMethod ?? project.revenueRecognitionMethod,
          startDate: editFormData.startDate ?? project.startDate,
          endDate: editFormData.endDate ?? project.endDate,
          clientIds: editFormData.clientIds ?? project.clientIds,
          projectManagerId: project.projectManagerId,
          health: project.health,
        };
        
        await updateProject(numericProjectId, updateData);
      }
      
      const updatedProject = { ...project, ...editFormData };
      setProject(updatedProject);
      setIsEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      toast.success('Project updated successfully');
      
      window.dispatchEvent(new CustomEvent('project-updated', { 
        detail: { projectId, project: updatedProject } 
      }));
    } catch (error) {
      toast.error('Failed to update project');
      console.error('Error saving project:', error);
    }
  };

  // Budget handlers
  const handleSaveBudget = async (
    updatedBudget: Partial<BudgetData>,
    revisionReason: string,
    saveAsBaseline: boolean
  ) => {
    console.log('Saving budget:', { updatedBudget, revisionReason, saveAsBaseline });
    
    try {
      const result = await updateBudget(numericProjectId, updatedBudget);
      
      if (result.success) {
        console.log('[ProjectDetails] Budget saved successfully, refetching data...');
        toast.success('Budget saved successfully!');
        refetchBudget();
      } else {
        console.error('[ProjectDetails] Failed to save budget:', result.error);
        toast.error(result.error || 'Failed to save budget');
      }
    } catch (error) {
      console.error('[ProjectDetails] Error saving budget:', error);
      toast.error('Failed to save budget');
    }
    
    // Close the appropriate modal
    setSalesBudgetEditModalOpen(false);
    setCostBudgetEditModalOpen(false);
    setResourceBudgetEditModalOpen(false);
  };

  const handleRestoreRevision = async (revisionId: string) => {
    console.log('Restoring revision:', revisionId);
    // TODO: Implement actual restore logic when backend is ready
    toast.success('Revision restored successfully!');
    setBudgetHistoryModalOpen(false);
  };

  const handleFieldChange = (field: keyof Project, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleClientChange = (clientId: string, selectedClient: Client | undefined) => {
    if (selectedClient) {
      // Update the client state
      setClient(selectedClient);
      
      // Update the form data with client ID
      setEditFormData(prev => ({ 
        ...prev, 
        clientIds: clientId 
      }));
      
      // Update recent clients list
      const updatedRecentClients = [
        clientId,
        ...recentClientIds.filter(id => id !== clientId)
      ].slice(0, 10); // Keep only the 10 most recent
      
      setRecentClientIds(updatedRecentClients);
      localStorage.setItem('recentClientIds', JSON.stringify(updatedRecentClients));
      
      setHasUnsavedChanges(true);
    }
  };

  // Warn user about unsaved changes when navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Listen for edit mode trigger from header Edit button
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener('project-edit-mode', handleEditMode);
    return () => window.removeEventListener('project-edit-mode', handleEditMode);
  }, [projectId]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [currentProject, clientsData, employeesData, resourceAllocationsData] = await Promise.all([
          fetchProjectById(numericProjectId),
          fetchClients('projects'),
          fetchEmployees('projects'),
          fetchResourceAllocations('projects'),
        ]);

        if (currentProject) {
          setProject(currentProject);
          setClients(clientsData); // Store all clients for the dropdown
          // Get the first client ID from the comma-separated list
          const firstClientId = currentProject.clientIds?.split(',')[0]?.trim();
          setClient(clientsData.find(c => c.id === parseInt(firstClientId || '0', 10)) || null);
          setProjectManager(employeesData.find(e => e.id === currentProject.projectManagerId) || null);
          
          // Get data mode to determine whether to use API or mock data
          const dataMode = getDataMode();
          
          // Get resource allocations for this project
          const projectAllocations = resourceAllocationsData.filter(r => r.projectId === numericProjectId);
          setResourceAllocations(projectAllocations);
          
          if (dataMode === 'live') {
            // Live mode - fetch data from API
            try {
                            const expenseOptions: FetchExpensesOptions = {
                              includeApprovedExpenses: includeApprovedExpenses,
                              includeCreditCardExpenses: includeCreditCardExpenses,
                            };
                            const [apiTimeEntries, apiExpenses, apiMilestones, apiChangeRequests] = await Promise.all([
                              fetchTimeEntriesByProjectId(numericProjectId),
                              fetchExpensesByProjectId(numericProjectId, currentProject.code, currentCompany, 'expenses', expenseOptions),
                              fetchMilestonesByProjectId(numericProjectId),
                              fetchChangeRequestsByProjectId(numericProjectId),
                            ]);
              setTimeEntries(apiTimeEntries);
              setExpenses(apiExpenses);
              setMilestones(apiMilestones);
              setChangeRequests(apiChangeRequests);
              
              // Calculate actual hours per employee from time entries
              const timeByEmployee = new Map<number, number>();
              apiTimeEntries.forEach((entry: TimeEntry) => {
                const current = timeByEmployee.get(entry.employeeId) || 0;
                timeByEmployee.set(entry.employeeId, current + entry.hours);
              });

              // Build a map of resource allocations by employee for quick lookup
              const allocationsByEmployee = new Map<number, typeof projectAllocations[0]>();
              projectAllocations.forEach(alloc => {
                allocationsByEmployee.set(alloc.employeeId, alloc);
              });

              // Generate team members from all employees fetched from the backend
              // This ensures the Team Management table shows employees even when there are no resource allocations
              const liveTeamMembers: TeamMember[] = employeesData.map((employee) => {
                const allocation = allocationsByEmployee.get(employee.id);
                const actualHours = timeByEmployee.get(employee.id) || 0;
                
                // Use allocation data if available, otherwise default to 0
                const allocatedHours = allocation?.allocatedHours || 0;
                const remaining = allocatedHours - actualHours;
                const utilisationPercent = allocatedHours > 0 ? (actualHours / allocatedHours) * 100 : 0;
                
                // Determine status based on utilisation
                let status: TeamMember['status'];
                if (allocatedHours > 0 && utilisationPercent > 100) {
                  status = 'Over Allocated';
                } else if (allocatedHours > 0 && utilisationPercent > 85) {
                  status = 'At Risk';
                } else {
                  status = 'On Track';
                }

                return {
                  id: allocation?.id || employee.id,
                  employeeId: employee.id,
                  name: employee.name,
                  email: employee.email || '',
                  role: allocation?.role || employee.role || 'Unassigned',
                  avatar: employee.photo,
                  allocatedHours,
                  actualHours,
                  remaining,
                  startDate: allocation?.startDate || '',
                  endDate: allocation?.endDate || '',
                  status,
                  utilisationPercent,
                };
              });
            
              // Set team members from employees
              setTeamMembers(liveTeamMembers);
            } catch (error) {
              console.error('Failed to fetch project details from API:', error);
              setTimeEntries([]);
              setExpenses([]);
              setMilestones([]);
              setChangeRequests([]);
              setTeamMembers([]);
            }
          } else {
            // Demo mode - generate mock data
            // Mock time entries - include both allocated and unallocated employees
            const mockTimeEntries: TimeEntry[] = projectAllocations.slice(0, 10).map((alloc, i) => {
              const isApproved = Math.random() > 0.3;
              const entryDate = new Date(Date.now() - i * 86400000);
              
              return {
                id: numericProjectId * 10000 + i,
                externalTimesheetId: `TS-2024-${1000 + i}`,
                projectId: numericProjectId,
                employeeId: alloc.employeeId,
                date: entryDate.toISOString().split('T')[0],
                hours: 6 + Math.random() * 3,
                description: 'Development work',
                billable: Math.random() > 0.2,
                approved: isApproved,
                approvalHistory: [
                  {
                    date: new Date(entryDate.getTime() + 3600000).toISOString(),
                    action: 'Submitted for Approval',
                    byUser: employeesData.find(e => e.id === alloc.employeeId)?.name || 'Employee',
                    status: 'Pending Approval',
                  },
                  ...(isApproved ? [{
                    date: new Date(entryDate.getTime() + 7200000).toISOString(),
                    action: 'Approved',
                    byUser: 'Project Manager',
                    toUser: 'Finance Team',
                    stage: 'Manager Approval',
                    status: 'Approved',
                  }] : []),
                ],
                costRate: 50 + Math.random() * 30,
                billRate: 100 + Math.random() * 60,
              };
            });
            
            // Add time entries from employees NOT allocated to the project (to demonstrate unallocated tracking)
            const mockAllocatedEmployeeIds = new Set(projectAllocations.map(a => a.employeeId));
            const unallocatedEmployees = employeesData.filter(e => !mockAllocatedEmployeeIds.has(e.id)).slice(0, 2);
            
            unallocatedEmployees.forEach((employee, i) => {
              // Add 3-5 time entries for each unallocated employee
              const entryCount = 3 + Math.floor(Math.random() * 3);
              for (let j = 0; j < entryCount; j++) {
                const isApproved = Math.random() > 0.4;
                const entryDate = new Date(Date.now() - (i * 7 + j) * 86400000);
                
                mockTimeEntries.push({
                  id: numericProjectId * 10000 + 1000 + i * 10 + j,
                  externalTimesheetId: `TS-2024-${2000 + i * 10 + j}`,
                  projectId: numericProjectId,
                  employeeId: employee.id,
                  date: entryDate.toISOString().split('T')[0],
                  hours: 4 + Math.random() * 4,
                  description: 'Ad-hoc support work',
                  billable: Math.random() > 0.3,
                  approved: isApproved,
                  approvalHistory: [
                    {
                      date: new Date(entryDate.getTime() + 3600000).toISOString(),
                      action: 'Submitted for Approval',
                      byUser: employee.name,
                      status: 'Pending Approval',
                    },
                    ...(isApproved ? [{
                      date: new Date(entryDate.getTime() + 7200000).toISOString(),
                      action: 'Approved',
                      byUser: 'Project Manager',
                      toUser: 'Finance Team',
                      stage: 'Manager Approval',
                      status: 'Approved',
                    }] : []),
                  ],
                  costRate: 50 + Math.random() * 30,
                  billRate: 100 + Math.random() * 60,
                });
              }
            });
            
            setTimeEntries(mockTimeEntries);
            
            // Mock expenses
            const mockExpenses: Expense[] = [
              {
                id: numericProjectId * 1000 + 1,
                projectId: numericProjectId,
                employeeId: projectAllocations[0]?.employeeId || 'e1',
                date: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0],
                category: 'Travel',
                amount: 450,
                description: 'Client site visit',
                billable: true,
                approved: true,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 3600000).toISOString(),
                    action: 'Submitted for Approval',
                    byUser: employeesData.find(e => e.id === (projectAllocations[0]?.employeeId || 'e1'))?.name || 'Employee',
                    status: 'Pending Approval',
                  },
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 7200000).toISOString(),
                    action: 'Approved',
                    byUser: 'Project Manager',
                    toUser: 'Finance Team',
                    stage: 'Manager Approval',
                    status: 'Approved',
                  },
                ],
              },
              {
                id: numericProjectId * 1000 + 2,
                projectId: numericProjectId,
                employeeId: projectAllocations[1]?.employeeId || 'e2',
                date: new Date(Date.now() - 10 * 86400000).toISOString().split('T')[0],
                category: 'Software',
                amount: 299,
                description: 'Development tools licence',
                billable: true,
                approved: false,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 10 * 86400000 + 3600000).toISOString(),
                    action: 'Submitted for Approval',
                    byUser: employeesData.find(e => e.id === (projectAllocations[1]?.employeeId || 'e2'))?.name || 'Employee',
                    status: 'Pending Approval',
                  },
                ],
              },
            ];
            setExpenses(mockExpenses);
            
            // Mock milestones
            const mockMilestones: Milestone[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                name: 'Project Kickoff',
                dueDate: currentProject.startDate,
                completedDate: currentProject.startDate,
                status: 'completed',
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                name: 'Phase 1 Delivery',
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 60 * 86400000).toISOString().split('T')[0],
                completedDate: new Date(new Date(currentProject.startDate).getTime() + 58 * 86400000).toISOString().split('T')[0],
                status: 'completed',
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 3,
                projectId: numericProjectId,
                name: 'Phase 2 Delivery',
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 120 * 86400000).toISOString().split('T')[0],
                status: currentProject.completionPercent > 70 ? 'completed' : 'pending',
                completedDate: currentProject.completionPercent > 70 ? new Date(new Date(currentProject.startDate).getTime() + 118 * 86400000).toISOString().split('T')[0] : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: false,
              },
              {
                id: numericProjectId * 100 + 4,
                projectId: numericProjectId,
                name: 'Final Delivery',
                dueDate: currentProject.endDate,
                status: currentProject.status === 'completed' ? 'completed' : 'pending',
                completedDate: currentProject.status === 'completed' ? currentProject.endDate : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: false,
              },
            ];
            setMilestones(mockMilestones);
            
            // Mock change requests
            const mockChangeRequests: ChangeRequest[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                title: 'Additional feature request',
                description: 'Client requested additional reporting functionality',
                requestedBy: 'Client',
                requestedDate: new Date(Date.now() - 15 * 86400000).toISOString().split('T')[0],
                status: 'approved',
                impactBudget: 15000,
                impactSchedule: 10,
                approvedBy: currentProject.projectManagerId,
                approvedDate: new Date(Date.now() - 10 * 86400000).toISOString().split('T')[0],
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                title: 'Scope clarification',
                description: 'Clarification on integration requirements',
                requestedBy: 'Project Team',
                requestedDate: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0],
                status: 'pending',
                impactBudget: 0,
                impactSchedule: 0,
              },
            ];
            setChangeRequests(mockChangeRequests);
            
            // Generate comprehensive team member data with mock hours
            const mockTeamMembers: TeamMember[] = projectAllocations.map((alloc) => {
              const employee = employeesData.find(e => e.id === alloc.employeeId);
              const allocatedHours = Math.floor(Math.random() * 200) + 100; // 100-300 hours
              const actualHours = Math.floor(allocatedHours * (0.3 + Math.random() * 0.8)); // 30-110% of allocated
              const remaining = allocatedHours - actualHours;
              const utilisationPercent = (actualHours / allocatedHours) * 100;
              
              // Determine status based on utilisation only
              let status: TeamMember['status'];
              
              if (utilisationPercent > 100) {
                status = 'Over Allocated';
              } else if (utilisationPercent > 85) {
                status = 'At Risk';
              } else {
                status = 'On Track';
              }
              
              // Generate avatar URL using UI Avatars service
              const name = employee?.name || 'Unknown';
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

              return {
                id: alloc.id,
                employeeId: alloc.employeeId,
                name: name,
                email: employee?.email || '',
                role: alloc.role,
                avatar: avatarUrl,
                allocatedHours,
                actualHours,
                remaining,
                startDate: alloc.startDate,
                endDate: alloc.endDate,
                status,
                utilisationPercent,
              };
            });
            
            // Find employees who logged time but weren't allocated
            const demoAllocatedEmployeeIds = new Set(mockTeamMembers.map(tm => tm.employeeId));
            const demoUnallocatedWithTime: TeamMember[] = [];
            
            // Group time entries by employee
            const demoTimeByEmployee = new Map<number, number>();
            mockTimeEntries.forEach(entry => {
              if (!demoAllocatedEmployeeIds.has(entry.employeeId)) {
                const current = demoTimeByEmployee.get(entry.employeeId) || 0;
                demoTimeByEmployee.set(entry.employeeId, current + entry.hours);
              }
            });
            
            // Create TeamMember entries for unallocated employees who logged time
            demoTimeByEmployee.forEach((hours, employeeId) => {
              const employee = employeesData.find(e => e.id === employeeId);
              if (employee) {
                const name = employee.name;
                const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;
                
                demoUnallocatedWithTime.push({
                  id: `unalloc-${employeeId}`,
                  employeeId: employee.id,
                  name: name,
                  email: employee.email || '',
                  role: employee.role || 'Unassigned', // Use employee.role instead of employee.position
                  avatar: avatarUrl,
                  allocatedHours: 0, // Not allocated
                  actualHours: hours,
                  remaining: -hours, // Negative because they worked without allocation
                  startDate: '', // No allocation dates
                  endDate: '',
                  status: 'Over Allocated', // Working without allocation
                  utilisationPercent: 0, // Can't calculate without allocation
                });
              }
            });
            
            // Combine allocated and unallocated team members
            setTeamMembers([...mockTeamMembers, ...demoUnallocatedWithTime]);
          }
          
          // Fetch cost centres - use API in live mode, mock data in demo mode
          // Note: dataMode is already defined above
          if (dataMode === 'live') {
            try {
              const apiCostCentres = await fetchCostCentresByProjectId(numericProjectId);
              setCostCentres(apiCostCentres);
            } catch (error) {
              console.error('Failed to fetch cost centres from API:', error);
              setCostCentres([]);
            }
          } else {
            // Helper arrays for variety (used by both flat and hierarchical views)
            const types = ['Functional Area', 'Administrative', 'Technical Support', 'Operations', 'Support Services'];
            const statuses: Array<'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled'> = ['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
            const departments = ['Engineering', 'Design', 'Quality Assurance', 'PMO', 'IT Operations', 'Marketing', 'Sales', 'Finance', 'HR', 'Legal'];
            const locations = ['London Office', 'Manchester Office', 'Birmingham Office', 'Head Office', 'Data Centre', 'Edinburgh Office', 'Bristol Office', 'Leeds Office', 'Remote', 'Client Site'];
            const priorities = ['High', 'Medium', 'Low'];
            const owners = ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Emily Brown', 'David Wilson', 'Alice Chen', 'Bob Martinez', 'Carol White', 'Dan Brown', 'Emma Wilson'];
            
            // Mock cost centres data for demo mode - 50 records for scrolling test
            const mockCostCentres: CostCentre[] = [];
            
            // Generate 50 cost centres
            for (let i = 1; i <= 50; i++) {
              const code = `CC-${String(i).padStart(3, '0')}`;
              const typeIndex = (i - 1) % types.length;
              const statusIndex = (i - 1) % statuses.length;
              const deptIndex = (i - 1) % departments.length;
              const locIndex = (i - 1) % locations.length;
              const priorityIndex = (i - 1) % priorities.length;
              const ownerIndex = (i - 1) % owners.length;
              
              // Vary the budget allocation
              const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
              const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3); // 85% to 115% of budget
              const committedFactor = budgetFactor * 0.1;
              
              mockCostCentres.push({
                id: -Date.now() - i,
                code: code,
                name: `Cost Centre ${i}`,
                description: `Description for cost centre ${code}`,
                type: types[typeIndex],
                status: statuses[statusIndex],
                billable: i % 3 !== 0, // 2/3 are billable
                costsBudget: currentProject.budgetCost * budgetFactor,
                actualCosts: currentProject.actualCost * actualFactor,
                committedCosts: currentProject.budgetCost * committedFactor,
                revenueBudget: currentProject.budgetRevenue * budgetFactor,
                actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                hoursBudget: Math.round(budgetFactor * 10000),
                actualHours: Math.round(actualFactor * 9500),
                owner: owners[ownerIndex],
                lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                createdAt: new Date(currentProject.startDate),
                updatedAt: new Date(Date.now() - (i % 10) * 86400000),
                customFields: {
                  'Department': departments[deptIndex],
                  'Location': locations[locIndex],
                  'Priority': priorities[priorityIndex],
                  'Start Date': new Date(currentProject.startDate),
                  'End Date': new Date(currentProject.endDate),
                  'Requires Approval': i % 2 === 0,
                  'Approval Threshold': 1000 + (i * 100),
                  'Cost Code': `${code}-${departments[deptIndex].substring(0, 3).toUpperCase()}`,
                  'GL Account': `${6100 + (i % 5) * 100}-${1000 + (i % 10) * 100}`,
                  'Project Phase': `Phase ${(i % 5) + 1}`,
                },
              });
            }
            setCostCentres(mockCostCentres);
            
            // Mock hierarchical cost centres data - includes all 50 cost centre codes
            const mockHierarchicalCostCentres: HierarchicalCostCentre[] = [];
            
            // Create 5 parent departments, each with 10 cost centres
            const parentDepartments: Array<{ code: string; name: string; range: [number, number] }> = [
              { code: 'CC-DEPT-01', name: 'Engineering Department', range: [1, 10] },
              { code: 'CC-DEPT-02', name: 'Design & Creative', range: [11, 20] },
              { code: 'CC-DEPT-03', name: 'Operations', range: [21, 30] },
              { code: 'CC-DEPT-04', name: 'Support Services', range: [31, 40] },
              { code: 'CC-DEPT-05', name: 'Administration', range: [41, 50] },
            ];
            
            parentDepartments.forEach((dept, deptIndex) => {
              // Calculate totals for parent
              const deptBudgetFactor = 0.2; // Each department gets 20% of total budget
            
              // Add parent department
              mockHierarchicalCostCentres.push({
                id: -Date.now() - 100 - deptIndex,
                code: dept.code,
                name: dept.name,
                billable: true,
                costsBudget: currentProject.budgetCost * deptBudgetFactor,
                actualCosts: currentProject.actualCost * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                committedCosts: currentProject.budgetCost * deptBudgetFactor * 0.05,
                revenueBudget: currentProject.budgetRevenue * deptBudgetFactor,
                actualRevenue: currentProject.actualRevenue * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                hoursBudget: Math.round(deptBudgetFactor * 10000),
                actualHours: Math.round(deptBudgetFactor * 9500 * (0.9 + Math.random() * 0.2)),
                parentId: null,
              });
              
              // Add child cost centres (CC-001 to CC-050)
              for (let i = dept.range[0]; i <= dept.range[1]; i++) {
                const code = `CC-${String(i).padStart(3, '0')}`;
                const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
                const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3);
                const committedFactor = budgetFactor * 0.1;
                
                const statusIndex = (i - 1) % statuses.length;
                const ownerIndex = (i - 1) % owners.length;
                
                  mockHierarchicalCostCentres.push({
                    id: -Date.now() - i,
                    code: code,
                  name: `Cost Centre ${i}`,
                  type: types[(i - 1) % types.length],
                  status: statuses[statusIndex],
                  billable: i % 3 !== 0,
                  costsBudget: currentProject.budgetCost * budgetFactor,
                  actualCosts: currentProject.actualCost * actualFactor,
                  committedCosts: currentProject.budgetCost * committedFactor,
                  revenueBudget: currentProject.budgetRevenue * budgetFactor,
                  actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                  hoursBudget: Math.round(budgetFactor * 10000),
                  actualHours: Math.round(actualFactor * 9500),
                  owner: owners[ownerIndex],
                  lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                  parentId: -Date.now() - 100 - deptIndex,
                });
              }
            });
            
            setHierarchicalCostCentres(mockHierarchicalCostCentres);
          }
        }

        setEmployees(employeesData);
      } catch (error) {
        handleApiError(error, `/api/projects/${projectId}`, 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [projectId, handleApiError]);

  // Sync expense checkbox defaults when company settings change
  useEffect(() => {
    if (currentCompany) {
      setIncludeApprovedExpenses(currentCompany.approvedExpensesEnabled ?? true);
      setIncludeCreditCardExpenses(currentCompany.creditCardExpensesEnabled ?? false);
    }
  }, [currentCompany?.id]);

  // Re-fetch expenses when checkbox selections change
  useEffect(() => {
    if (!project || loading) return;
    const dataMode = getDataMode();
    if (dataMode !== 'live') return;

    async function refetchExpenses() {
      setExpensesLoading(true);
      try {
        const expenseOpts: FetchExpensesOptions = {
          includeApprovedExpenses: includeApprovedExpenses,
          includeCreditCardExpenses: includeCreditCardExpenses,
        };
        const refreshedExpenses = await fetchExpensesByProjectId(
          numericProjectId, project!.code, currentCompany, 'expenses', expenseOpts
        );
        setExpenses(refreshedExpenses);
      } catch (error) {
        console.error('Failed to refetch expenses:', error);
      } finally {
        setExpensesLoading(false);
      }
    }
    refetchExpenses();
  }, [includeApprovedExpenses, includeCreditCardExpenses]);

  // Listen for edit mode event from the Edit button in AppLayout
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      console.log('Edit mode event received:', customEvent.detail, 'Current projectId:', projectId);
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener('project-edit-mode', handleEditMode);
    return () => window.removeEventListener('project-edit-mode', handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Warn user about unsaved changes when navigating away
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load recent client IDs from localStorage
  useEffect(() => {
    const storedRecentClients = localStorage.getItem('recentClientIds');
    if (storedRecentClients) {
      try {
        const parsed = JSON.parse(storedRecentClients);
        setRecentClientIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error('Failed to parse recent client IDs:', error);
        setRecentClientIds([]);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton for Tabs */}
        <Card>
          <CardHeader>
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="pt-4">
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-muted-foreground">Project not found</div>
        <Button onClick={() => onBack ? onBack() : navigate(ROUTES.PROJECTS)}>
          Back to Projects
        </Button>
      </div>
    );
  }

  const getEmployeeName = (employeeId: number) => {
    return employees.find(e => e.id === employeeId)?.name || 'Unknown';
  };
  
  const getStatusBadgeVariant = (status: TeamMember['status'], utilization?: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'On Track':
        // Use different colors based on utilization
        if (utilization !== undefined) {
          if (utilization > 75) {
            return 'default'; // Blue for higher utilization (75-85%)
          } else if (utilization > 50) {
            return 'outline'; // Outline for medium utilization (50-75%)
          } else {
            return 'secondary'; // Gray for low utilization (0-50%)
          }
        }
        return 'default';
      case 'At Risk':
        return 'destructive';
      case 'Over Allocated':
        return 'destructive';
      default:
        return 'default';
    }
  };
  
  const getStatusColor = (status: TeamMember['status'] | string): string => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'completed':
        return 'text-neutral-600';
      case 'on track':
      case 'on-track':
        return 'text-success-600';
      case 'active':
      case 'in-progress':
        return 'text-info-600';
      case 'at risk':
      case 'at-risk':
        return 'text-warning-600';
      case 'over allocated':
      case 'over-allocated':
        return 'text-error-600';
      case 'planning':
      case 'on-hold':
        return 'text-neutral-500';
      default:
        return 'text-neutral-600';
    }
  };
  
  const handleSort = (field: keyof TeamMember) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRemoveTeamMember = async (member: TeamMember) => {
    // Only allow removal if no hours have been logged
    if (member.actualHours > 0) {
      toast.error('Cannot remove team member who has logged hours');
      return;
    }

    const dataMode = getDataMode();
    
    // In live mode, call the API to delete the resource allocation
    if (dataMode === 'live' && member.id && member.id !== member.employeeId) {
      try {
        const memberId = typeof member.id === 'number' ? member.id : parseInt(String(member.id), 10);
        await deleteResourceAllocation(memberId);
      } catch (error) {
        console.error('Failed to delete resource allocation:', error);
        toast.error('Failed to remove team member from project');
        return;
      }
    }

    // Remove the team member from local state
    setTeamMembers(prev => prev.filter(tm => tm.id !== member.id));

    // Update the budget data to reflect the removed allocation
    if (enrichedBudgetData?.resourceBudget?.byRole && member.allocatedHours > 0) {
      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map(r => {
        if (r.role === member.role) {
          return {
            ...r,
            allocatedHours: Math.max(0, r.allocatedHours - member.allocatedHours),
          };
        }
        return r;
      });

      // Update local budget data
      setLocalBudgetData({
        ...enrichedBudgetData,
        resourceBudget: {
          ...enrichedBudgetData.resourceBudget,
          byRole: updatedRoles,
        },
      });
    }

    toast.success(`${member.name} removed from project`);
  };
  
  const sortedTeamMembers = [...teamMembers].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    }
    
    return 0;
  });
  
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const totalBillableHours = timeEntries
    .filter(t => t.billable)
    .reduce((sum, t) => sum + t.hours, 0);

  const totalNonBillableHours = timeEntries
    .filter(t => !t.billable)
    .reduce((sum, t) => sum + t.hours, 0);

  const totalExpenseAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const overdueMilestones = milestones.filter(m => m.status === 'overdue').length;

  const activeResources = resourceAllocations.filter(r => r.status === 'confirmed').length;

  const pendingChangeRequests = changeRequests.filter(cr => cr.status === 'pending').length;
  const approvedChangeRequests = changeRequests.filter(cr => cr.status === 'approved').length;

  // Create default empty budget data for new projects that don't have budget data yet
  const getDefaultBudgetData = (): BudgetData => ({
    projectId: numericProjectId,
    projectName: project?.name || 'New Project',
    projectType: (project?.type as 'fixed-price' | 'time-and-materials' | 'retainer') || 'fixed-price',
    summary: {
      revenue: { budget: 0, actual: 0, variance: 0, variancePercent: 0, progressPercent: 0, trend: 'stable', status: 'green' },
      costs: { budget: 0, actual: 0, variance: 0, variancePercent: 0, progressPercent: 0, trend: 'stable', status: 'green' },
      margin: { budgetedMargin: 0, actualMargin: 0, variance: 0, status: 'green' },
      hours: { budget: 0, actual: 0, remaining: 0, progressPercent: 0, status: 'green' },
      health: { score: 'green', statusText: 'On Track', factors: [] },
    },
    costBudget: {
      projectId: numericProjectId,
      categories: [],
      phases: [],
      totals: { budget: 0, actual: 0, committed: 0, available: 0, progressPercent: 0 },
    },
    salesBudget: {
      projectId: numericProjectId,
      projectType: (project?.type as 'fixed-price' | 'time-and-materials' | 'retainer') || 'fixed-price',
    },
    resourceBudget: {
      projectId: numericProjectId,
      summary: {
        totalBudget: 0,
        totalActual: 0,
        totalRemaining: 0,
        progressPercent: 0,
        billablePercent: 0,
        targetBillablePercent: 80,
        totalBudgetCost: 0,
        totalActualCost: 0,
      },
      byRole: [],
      byPerson: [],
      byPhase: [],
    },
    allBudgets: {
      projectId: numericProjectId,
      profitability: {
        revenue: { budget: 0, actual: 0, variance: 0 },
        directCosts: { budget: 0, actual: 0, variance: 0 },
        grossProfit: { budget: 0, actual: 0, variance: 0 },
        grossMargin: { budget: 0, actual: 0, variance: 0 },
        overhead: { budget: 0, actual: 0, variance: 0 },
        netProfit: { budget: 0, actual: 0, variance: 0 },
        netMargin: { budget: 0, actual: 0, variance: 0 },
      },
      burnRateData: [],
      forecast: {
        projectedFinalRevenue: 0,
        projectedFinalCost: 0,
        projectedFinalMargin: 0,
        revenueVariance: 0,
        costVariance: 0,
        marginVariance: 0,
      },
    },
    alerts: [],
    revisions: [],
    lastUpdated: new Date().toISOString(),
  });

  // Show full-screen edit page if editing cost budget
  if (editCostBudgetPageOpen) {
    const budgetDataToUse = budgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const projectDisplayName = project?.code 
      ? `${project.code} - ${project.name}` 
      : project?.name || budgetDataToUse.projectName;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditCostBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log('[ProjectDetails] Saving cost budget:', updatedBudget);
          
          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);
            
            if (result.success) {
              toast.success('Cost budget saved successfully');
              
              // Close the edit page
              setEditCostBudgetPageOpen(false);
              
              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error('Failed to save cost budget');
            }
          } catch (error) {
            console.error('[ProjectDetails] Error saving budget:', error);
            toast.error('Failed to save cost budget');
          }
        }}
        onCancel={() => setEditCostBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing sales budget (T&M projects only)
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editSalesBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code ? `${project.code} - ${project.name}` : project?.name || `Project ${projectId}`;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditSalesBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log('[ProjectDetails] Saving sales budget:', updatedBudget);
          
          const result = await updateBudget(project.id, updatedBudget);
          
          if (result.success) {
            toast.success('Sales budget saved successfully');
            
            // Close the edit page
            setEditSalesBudgetPageOpen(false);
            
            // Refetch the budget data to show updated values
            refetchBudget();
          } else {
            throw new Error(result.error || 'Failed to save sales budget');
          }
        }}
        onCancel={() => setEditSalesBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing resource budget
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editResourceBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const resourceProjectDisplayName = project?.code 
      ? `${project.code} - ${project.name}` 
      : project?.name || budgetDataToUse.projectName;
    const resourceBudgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: resourceProjectDisplayName,
    };
    return (
      <EditResourceBudgetPage
        budgetData={resourceBudgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log('[ProjectDetails] Saving resource budget:', updatedBudget);
          
          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);
            
            if (result.success) {
              toast.success('Resource budget saved successfully');
              
              // Close the edit page
              setEditResourceBudgetPageOpen(false);
              
              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error('Failed to save resource budget');
            }
          } catch (error) {
            console.error('[ProjectDetails] Error saving budget:', error);
            toast.error('Failed to save resource budget');
          }
        }}
        onCancel={() => setEditResourceBudgetPageOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-6" style={{ paddingBottom: isEditMode ? '80px' : '0' }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onBack ? onBack() : navigate(ROUTES.PROJECTS)}
            className="h-8 w-8 mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeaderBlock
            title={project.code ? `${project.code} - ${project.name}` : project.name}
            subtitle={project.description || 'Project details and management'}
            size={20}
            showActions={false}
          />
        </div>
        <Button
          variant="outline"
          onClick={handleEnterEditMode}
          disabled={isEditMode}
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Project
        </Button>
      </div>

      {/* Edit Mode Indicator */}
      {isEditMode && (
        <div className="mb-4 p-3 bg-info-50 border border-info-200 rounded-lg flex items-center gap-2">
          <Edit className="h-4 w-4 text-info-700" />
          <span className="text-sm font-medium text-info-700">Edit Mode Active</span>
          <span className="text-sm text-info-600">Make your changes and click Save when done.</span>
        </div>
      )}

      {/* Tabs */}
      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="financials" className="flex items-center gap-2">
              <Landmark className="h-4 w-4" />
              Financials
            </TabsTrigger>
            {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
              <TabsTrigger value="sales-budget" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Sales Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
              <TabsTrigger value="cost-budget" className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                Cost Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && isResourceBudgetEnabled && (
              <TabsTrigger value="resource-budget" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Resource Budget
              </TabsTrigger>
            )}
            <TabsTrigger value="cost-centres" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Cost Centres
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <UsersRound className="h-4 w-4" />
              Team
            </TabsTrigger>
            <TabsTrigger value="milestones" className="flex items-center gap-2">
              <MilestoneIcon className="h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="changes" className="flex items-center gap-2">
              <GitPullRequestArrow className="h-4 w-4" />
              Change Requests
            </TabsTrigger>
          </TabsList>

          {/* Scrollable Tab Content Container */}
          <div ref={tabsContainerRef}>
            <ScrollableTabsContainer height={`${tabsContainerHeight}px`}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
          {/* Key Metrics - Collapsible */}
          <Collapsible open={isKpiOpen} onOpenChange={setIsKpiOpen}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Key Metrics</h2>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-9 p-0">
                  {isKpiOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle KPI cards</span>
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue Budget</CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-2xl font-bold truncate">
                      <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-sm">{formatCurrency(project.budgetRevenue)}</p>
                  </TooltipContent>
                </Tooltip>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  Actual: <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                </p>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  Cost Budget: <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Cost Variance</CardTitle>
                {project.variancePercent < 0 ? (
                  <TrendingDown className="h-4 w-4 text-error-500" />
                ) : (
                  <TrendingUp className="h-4 w-4 text-success-500" />
                )}
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getVarianceColor(project.variancePercent)}`}>
                  {project.variancePercent > 0 ? '+' : ''}
                  {formatPercent(project.variancePercent)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  <SensitiveData>{formatCurrency(Math.abs(project.variance))}</SensitiveData>{' '}
                  {project.variance < 0 ? 'over budget' : 'under budget'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Margin</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Profit: <SensitiveData>{formatCurrency(project.actualRevenue - project.actualLabour - project.actualExpense)}</SensitiveData>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Hours Budget</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{project.budgetHours.toLocaleString()} hrs</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Actual: {project.actualHours.toLocaleString()} hrs
                </p>
                <p className={`text-xs mt-1 ${getVarianceColor(project.hoursVariancePercent)}`}>
                  {project.hoursVariance > 0 ? '+' : ''}{project.hoursVariance.toLocaleString()} hrs ({project.hoursVariancePercent > 0 ? '+' : ''}{formatPercent(project.hoursVariancePercent)})
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completion</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(project.completionPercent, 0)}</div>
                <Progress value={project.completionPercent} className="mt-2" />
              </CardContent>
            </Card>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Project Information */}
            <Card className={isEditMode ? 'ring-2 ring-info-200 bg-info-50/30' : ''}>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isEditMode ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Project Name</Label>
                      <Input
                        id="edit-name"
                        value={editFormData.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-description">Description</Label>
                      <Textarea
                        id="edit-description"
                        value={editFormData.description || ''}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-status">Status</Label>
                      <Select
                        value={editFormData.status || project.status}
                        onValueChange={(value) => handleFieldChange('status', value)}
                      >
                        <SelectTrigger id="edit-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="in-progress">In Progress</SelectItem>
                          <SelectItem value="on-hold">On Hold</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-type">Project Type</Label>
                      <Select
                        value={editFormData.type || project.type}
                        onValueChange={(value) => handleFieldChange('type', value)}
                      >
                        <SelectTrigger id="edit-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed-price">Fixed Price</SelectItem>
                          <SelectItem value="time-and-materials">Time & Materials</SelectItem>
                          <SelectItem value="retainer">Retainer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-revenue-method">Revenue Recognition</Label>
                      <Select
                        value={editFormData.revenueRecognitionMethod || project.revenueRecognitionMethod}
                        onValueChange={(value) => handleFieldChange('revenueRecognitionMethod', value)}
                      >
                        <SelectTrigger id="edit-revenue-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage-of-completion">Percentage of Completion</SelectItem>
                          <SelectItem value="completed-contract">Completed Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-start-date">Start Date</Label>
                      <Input
                        id="edit-start-date"
                        type="date"
                        value={formatDateForInput(editFormData.startDate || project.startDate)}
                        onChange={(e) => handleFieldChange('startDate', e.target.value)}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label htmlFor="edit-end-date">End Date</Label>
                      <Input
                        id="edit-end-date"
                        type="date"
                        value={formatDateForInput(editFormData.endDate || project.endDate)}
                        onChange={(e) => handleFieldChange('endDate', e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant={project.status === 'in-progress' ? 'default' : 'secondary'}>
                        {formatStatus(project.status)}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Project Type</span>
                      <span className="font-medium">{formatProjectType(project.type)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revenue Recognition</span>
                      <span className="font-medium">
                        {formatRevenueRecognition(project.revenueRecognitionMethod)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Start Date</span>
                      <span className="font-medium">{formatDate(project.startDate)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">End Date</span>
                      <span className="font-medium">{formatDate(project.endDate)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Project Manager</span>
                      <span className="font-medium">{projectManager?.name || 'Unknown'}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Client Information */}
            <Card className={cn(isEditMode && "ring-2 ring-primary/20 bg-primary/5")}>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditMode ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="client-name">Client Name</Label>
                        <ClientCombobox
                          clients={clients}
                          value={client?.id !== undefined ? String(client.id) : undefined}
                          onValueChange={handleClientChange}
                        placeholder="Select a client..."
                        recentClientIds={recentClientIds}
                        maxRecentClients={5}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Industry</Label>
                      <div className="text-sm text-muted-foreground">
                        {client?.industry || 'Select a client to see industry'}
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Contact Name</Label>
                      <div className="text-sm text-muted-foreground">
                        {client?.contactName || 'Select a client to see contact'}
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-2">
                      <Label>Contact Email</Label>
                      <div className="text-sm text-muted-foreground">
                        {client?.contactEmail || 'Select a client to see email'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Client Name</span>
                      <span className="font-medium">{client?.name || 'Unknown'}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Industry</span>
                      <span className="font-medium">{client?.industry || 'Unknown'}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Contact</span>
                      <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email</span>
                      <span className="font-medium text-sm">{client?.contactEmail || 'Unknown'}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Active Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{activeResources}</div>
                <p className="text-xs text-muted-foreground">Team members</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Milestones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {completedMilestones}/{milestones.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  {overdueMilestones > 0 && `${overdueMilestones} overdue`}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Change Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{changeRequests.length}</div>
                <p className="text-xs text-muted-foreground">
                  {pendingChangeRequests} pending, {approvedChangeRequests} approved
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">WIP Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground">
                  Unbilled: <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" className="space-y-4">
          {/* Enhanced Financial Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Budget: <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                </p>
                <div className="flex items-center gap-1 mt-2">
                  {project.actualRevenue >= project.budgetRevenue ? (
                    <TrendingUp className="h-3 w-3 text-success-500" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-error-500" />
                  )}
                  <span className={`text-xs ${project.actualRevenue >= project.budgetRevenue ? 'text-success-600' : 'text-error-600'}`}>
                    {formatPercent(((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Costs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.totalActual)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Budget: <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                </p>
                <div className="flex items-center gap-1 mt-2">
                  {project.totalActual <= (project.budgetLabour + project.budgetExpense) ? (
                    <TrendingDown className="h-3 w-3 text-success-500" />
                  ) : (
                    <TrendingUp className="h-3 w-3 text-error-500" />
                  )}
                  <span className={`text-xs ${project.totalActual <= (project.budgetLabour + project.budgetExpense) ? 'text-success-600' : 'text-error-600'}`}>
                    {formatPercent(((project.totalActual - (project.budgetLabour + project.budgetExpense)) / (project.budgetLabour + project.budgetExpense)) * 100)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Gross Profit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <SensitiveData>{formatCurrency(project.actualRevenue - project.totalActual)}</SensitiveData>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Margin: <SensitiveData>{formatPercent(project.marginPercent)}</SensitiveData>
                </p>
                <div className="flex items-center gap-1 mt-2">
                  {project.marginPercent >= 30 ? (
                    <>
                      <CheckCircle className="h-3 w-3 text-success-500" />
                      <span className="text-xs text-success-600">Healthy</span>
                    </>
                  ) : project.marginPercent >= 15 ? (
                    <>
                      <AlertCircle className="h-3 w-3 text-warning-500" />
                      <span className="text-xs text-warning-600">At Risk</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3 text-error-500" />
                      <span className="text-xs text-error-600">Critical</span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Billing & WIP</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Billed to Date */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Billed to Date</span>
                    <span className="text-sm font-semibold">
                      <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-muted-foreground">of {formatCurrency(project.budgetRevenue)}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatPercent((project.actualRevenue / project.budgetRevenue) * 100)}
                    </span>
                  </div>
                </div>

                <Separator />

                {/* WIP & Unbilled */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">WIP (Unbilled)</span>
                    <span className="text-sm font-semibold text-warning-600">
                      <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-muted-foreground">Ready to invoice</span>
                    <span className="text-xs font-medium text-success-600">
                      <SensitiveData>{formatCurrency(project.unbilledAmount)}</SensitiveData>
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Status Indicator */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const billingPercent = (project.actualRevenue / project.budgetRevenue) * 100;
                    const completionPercent = project.completionPercent || 0;
                    const billingGap = billingPercent - completionPercent;
                    
                    // Check WIP aging (simulate: if WIP > £50k, assume some is aged)
                    const hasAgedWIP = project.wipAmount > 50000;
                    
                    if (hasAgedWIP) {
                      return (
                        <>
                          <Clock className="h-3 w-3 text-error-500" />
                          <span className="text-xs text-error-600">WIP aging - invoice now</span>
                        </>
                      );
                    } else if (billingGap < -10) {
                      return (
                        <>
                          <AlertCircle className="h-3 w-3 text-error-500" />
                          <span className="text-xs text-error-600">Under-billing by {Math.abs(billingGap).toFixed(0)}%</span>
                        </>
                      );
                    } else if (billingGap < -5) {
                      return (
                        <>
                          <AlertCircle className="h-3 w-3 text-warning-500" />
                          <span className="text-xs text-warning-600">Slightly under-billing</span>
                        </>
                      );
                    } else if (project.wipAmount > 30000) {
                      return (
                        <>
                          <AlertCircle className="h-3 w-3 text-warning-500" />
                          <span className="text-xs text-warning-600">High WIP - review billing</span>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <CheckCircle className="h-3 w-3 text-success-500" />
                          <span className="text-xs text-success-600">Billing on track</span>
                        </>
                      );
                    }
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visual Charts Section */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Profitability Waterfall Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Profitability Breakdown</CardTitle>
                <CardDescription>Revenue to profit waterfall</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart
                      data={[
                        { name: 'Revenue', value: project.actualRevenue, fill: '#22c55e' },
                        { name: 'Labour Costs', value: -project.actualLabour, fill: '#f97316' },
                        { name: 'Expenses', value: -project.actualExpense, fill: '#3b82f6' },
                        { name: 'Gross Profit', value: project.actualRevenue - project.totalActual, fill: project.actualRevenue - project.totalActual > 0 ? '#22c55e' : '#f97316' },
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="name" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {payload[0].payload.name}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {formatCurrency(Math.abs(payload[0].value as number))}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {[
                          { name: 'Revenue', value: project.actualRevenue, fill: '#22c55e' },
                          { name: 'Labour Costs', value: -project.actualLabour, fill: '#f97316' },
                          { name: 'Expenses', value: -project.actualExpense, fill: '#3b82f6' },
                          { name: 'Gross Profit', value: project.actualRevenue - project.totalActual, fill: project.actualRevenue - project.totalActual > 0 ? '#22c55e' : '#f97316' },
                        ].map((entry, index) => (
                          <RechartsCell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Margin Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Margin Trend</CardTitle>
                <CardDescription>6-month margin performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={(() => {
                        const months: { month: string; margin: number; target: number }[] = [];
                        const currentMargin = project.marginPercent;
                        const startMargin = currentMargin - 3 + Math.random() * 2;
                        
                        for (let i = 5; i >= 0; i--) {
                          const date = new Date();
                          date.setMonth(date.getMonth() - i);
                          const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
                          
                          let margin;
                          if (i === 0) {
                            margin = currentMargin;
                          } else {
                            const progress = (5 - i) / 5;
                            margin = startMargin + (currentMargin - startMargin) * progress + (Math.random() - 0.5) * 2;
                          }
                          
                          months.push({
                            month: monthName,
                            margin: Math.max(0, margin),
                            target: 30,
                          });
                        }
                        return months;
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {payload[0].payload.month}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      Margin: {formatPercent(payload[0].value as number)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      Target: {formatPercent(payload[0].payload.target)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="target" 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeDasharray="5 5"
                        strokeWidth={1}
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="margin" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cost Breakdown and Budget vs Actual */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Cost Breakdown Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
                <CardDescription>Distribution of project costs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={[
                          { name: 'Labour', value: project.actualLabour, fill: '#f97316' },
                          { name: 'Expenses', value: project.actualExpense, fill: '#3b82f6' },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {[
                          { name: 'Labour', value: project.actualLabour, fill: '#f97316' },
                          { name: 'Expenses', value: project.actualExpense, fill: '#3b82f6' },
                        ].map((entry, index) => (
                          <RechartsCell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {payload[0].name}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {formatCurrency(payload[0].value as number)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {formatPercent((payload[0].value as number) / project.totalActual * 100)} of total
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-1))' }} />
                      <span>Labour</span>
                    </div>
                    <span className="font-medium">
                      <SensitiveData>{formatCurrency(project.actualLabour)}</SensitiveData>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-2))' }} />
                      <span>Expenses</span>
                    </div>
                    <span className="font-medium">
                      <SensitiveData>{formatCurrency(project.actualExpense)}</SensitiveData>
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Budget Burndown Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Budget Burndown</CardTitle>
                <CardDescription>Budget consumption vs planned burn rate</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart
                      data={(() => {
                        // Calculate burndown data
                        const totalBudget = project.budgetLabour + project.budgetExpense;
                        const startDate = new Date(project.startDate);
                        const endDate = new Date(project.endDate);
                        const today = new Date();
                        
                        // Calculate project duration in days
                        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                        const daysElapsed = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                        
                        // Generate monthly data points
                        const months: { month: string; planned: number; actual: number; budget: number }[] = [];
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        
                        for (let i = 0; i <= 6; i++) {
                          const date = new Date(startDate);
                          date.setMonth(date.getMonth() + i);
                          
                          const monthDaysElapsed = Math.ceil((date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                          const plannedBurn = totalBudget * (monthDaysElapsed / totalDays);
                          
                          // Actual burn is slightly higher (simulating overrun)
                          const actualBurn = i === 0 ? 0 : plannedBurn * 1.15; // 15% over planned
                          
                          months.push({
                            month: monthNames[date.getMonth()],
                            planned: Math.min(plannedBurn, totalBudget),
                            actual: Math.min(actualBurn, project.totalActual),
                            budget: totalBudget,
                          });
                        }
                        
                        return months;
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {payload[0].payload.month}
                                    </span>
                                    <span className="font-bold text-success-600">
                                      Planned: {formatCurrency(payload[0].payload.planned)}
                                    </span>
                                    <span className="font-bold text-error-600">
                                      Actual: {formatCurrency(payload[0].payload.actual)}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      Budget: {formatCurrency(payload[0].payload.budget)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <ReferenceLine 
                        y={project.budgetLabour + project.budgetExpense} 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeDasharray="3 3"
                        label={{ value: 'Budget', position: 'right', fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="planned" 
                        stroke="#22c55e" 
                        strokeWidth={2}
                        dot={{ fill: '#22c55e', r: 3 }}
                        name="Planned Burn"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="actual" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        dot={{ fill: '#f97316', r: 3 }}
                        name="Actual Burn"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Budget vs Actual Table */}
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Actual</CardTitle>
                <CardDescription>Detailed variance analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Revenue</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualRevenue - project.budgetRevenue)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Labour</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetLabour)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualLabour)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualLabour - project.budgetLabour) / project.budgetLabour * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualLabour - project.budgetLabour)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Expenses</TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.budgetExpense)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <SensitiveData>{formatCurrency(project.actualExpense)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={getVarianceColor((project.actualExpense - project.budgetExpense) / project.budgetExpense * 100)}>
                          <SensitiveData>
                            {formatCurrency(project.actualExpense - project.budgetExpense)}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-t-2">
                      <TableCell className="font-bold">Total Costs</TableCell>
                      <TableCell className="text-right font-bold">
                        <SensitiveData>{formatCurrency(project.budgetLabour + project.budgetExpense)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <SensitiveData>{formatCurrency(project.totalActual)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <span className={getVarianceColor(project.variancePercent)}>
                          <SensitiveData>{formatCurrency(project.variance)}</SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-t-2">
                      <TableCell className="font-bold">Gross Profit</TableCell>
                      <TableCell className="text-right font-bold">
                        <SensitiveData>{formatCurrency(project.budgetRevenue - (project.budgetLabour + project.budgetExpense))}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <SensitiveData>{formatCurrency(project.actualRevenue - project.totalActual)}</SensitiveData>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <span className={getVarianceColor(((project.actualRevenue - project.totalActual) - (project.budgetRevenue - (project.budgetLabour + project.budgetExpense))) / (project.budgetRevenue - (project.budgetLabour + project.budgetExpense)) * 100)}>
                          <SensitiveData>
                            {formatCurrency((project.actualRevenue - project.totalActual) - (project.budgetRevenue - (project.budgetLabour + project.budgetExpense)))}
                          </SensitiveData>
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* T&M Sales Budget Tab */}
        {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
          <TabsContent value="sales-budget" className="space-y-4">
            {budgetLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading budget data...</p>
                </div>
              </div>
            ) : budgetError ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-4">
                  <div className="text-destructive mb-2">Error loading budget data</div>
                  <p className="text-sm text-muted-foreground">{budgetError}</p>
                </div>
              </div>
            ) : (
              <SalesBudgetView 
                projectId={numericProjectId}
                data={budgetData?.salesBudget}
                costBudget={budgetData?.costBudget}
                projectType={project.type}
                onCreateBudget={() => setSalesBudgetEditModalOpen(true)}
                onEdit={() => {
                  // Use page view for T&M projects, modal for Fixed Price
                  if (project.type === 'time-and-materials') {
                    setEditSalesBudgetPageOpen(true);
                  } else {
                    setSalesBudgetEditModalOpen(true);
                  }
                }}
                onExport={() => { setActiveBudgetType('sales'); setBudgetExportModalOpen(true); }}
                onHistory={() => { setActiveBudgetType('sales'); setBudgetHistoryModalOpen(true); }}
              />
            )}
          </TabsContent>
        )}

        {/* Cost Budget Tab */}
        {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
          <TabsContent value="cost-budget" className="space-y-4">
            {budgetLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading budget data...</p>
                </div>
              </div>
            ) : budgetError ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-4">
                  <div className="text-destructive mb-2">Error loading budget data</div>
                  <p className="text-sm text-muted-foreground">{budgetError}</p>
                </div>
              </div>
            ) : budgetData ? (
              <>
                <CostBudgetView 
                  projectId={numericProjectId}
                  resourceBudget={budgetData.resourceBudget}
                  data={budgetData.costBudget}
                  onEdit={() => setEditCostBudgetPageOpen(true)}
                  onExport={() => { setActiveBudgetType('cost'); setBudgetExportModalOpen(true); }}
                  onHistory={() => { setActiveBudgetType('cost'); setBudgetHistoryModalOpen(true); }}
                />
                
                {budgetData.alerts && budgetData.alerts.length > 0 && (
                  <BudgetAlertsSection alerts={budgetData.alerts} />
                )}
              </>
            ) : (
              <div className="flex items-center justify-center min-h-[500px]">
                <Card className="w-full max-w-md border-dashed">
                  <CardContent className="flex flex-col items-center justify-center text-center p-12">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Receipt className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                      Track expenses, commitments, and cost categories by creating a cost budget for this project.
                    </p>
                    <Button onClick={() => setEditCostBudgetPageOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Cost Budget
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        )}

        {/* Resource Budget Tab */}
        {isBudgetingEnabled && isResourceBudgetEnabled && (
          <TabsContent value="resource-budget" className="space-y-4">
            {budgetLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading budget data...</p>
                </div>
              </div>
            ) : budgetError ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-4">
                  <div className="text-destructive mb-2">Error loading budget data</div>
                  <p className="text-sm text-muted-foreground">{budgetError}</p>
                </div>
              </div>
            ) : budgetData && enrichedBudgetData?.resourceBudget ? (
              <>
                <ResourceBudgetWrapper 
                  projectId={numericProjectId}
                  data={enrichedBudgetData.resourceBudget}
                  onEdit={() => setEditResourceBudgetPageOpen(true)}
                  onExport={() => { setActiveBudgetType('resource'); setBudgetExportModalOpen(true); }}
                  onHistory={() => { setActiveBudgetType('resource'); setBudgetHistoryModalOpen(true); }}
                  availablePeople={employees}
                  existingAllocations={resourceAllocations}
                  projectStartDate={project?.startDate}
                  projectEndDate={project?.endDate}
                  onAssign={async (role, selections) => {
                    const dataMode = getDataMode();
                    const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                    if (dataMode === 'live') {
                      try {
                        const now = new Date();
                        const startDate = project?.startDate || now.toISOString().split('T')[0];
                        const endDate = project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

                        for (const selection of selections) {
                          const allocationData = {
                            projectId: numericProjectId,
                            employeeId: selection.personId,
                            role: role,
                            allocatedHours: selection.allocatedHours,
                            startDate: startDate,
                            endDate: endDate,
                            status: 'confirmed' as const,
                          };
                          await createResourceAllocation(allocationData);
                        }

                        let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                        if (!resourceBudgetId) {
                          console.warn('[onAssign/ResourceBudget] resourceBudgetId not found in local state, fetching from API...');
                          resourceBudgetId = (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                        }
                        if (resourceBudgetId) {
                          const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(r => r.role === role);
                          for (const selection of selections) {
                            const employee = employees.find(e => e.id === selection.personId);
                            try {
                              await createPersonBudget({
                                resourceBudgetId,
                                personId: selection.personId,
                                personName: employee?.name || selection.personName,
                                roleId: matchedRole?.roleId,
                                budgetHours: selection.allocatedHours,
                                costRate: 0,
                              });
                            } catch (pbError) {
                              console.warn('Failed to create PersonBudget record:', pbError);
                            }
                          }
                        } else {
                          console.warn('[onAssign/ResourceBudget] No resourceBudgetId available — skipping PersonBudget creation');
                        }
                      } catch (error) {
                        console.error('Failed to create resource allocations:', error);
                        toast.error('Failed to assign team members to project');
                        return;
                      }
                    }

                    if (enrichedBudgetData?.resourceBudget?.byRole) {
                      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map(r => {
                        if (r.role === role) {
                          return { ...r, allocatedHours: r.allocatedHours + totalAssignedHours };
                        }
                        return r;
                      });

                      const newPersonBudgets = selections.map(selection => {
                        const employee = employees.find(e => e.id === selection.personId);
                        return {
                          id: -Date.now() - selection.personId,
                          personId: selection.personId,
                          personName: employee?.name || selection.personName,
                          role: role,
                          budgetHours: selection.allocatedHours,
                          actualHours: 0,
                          utilisation: 0,
                          costRate: 0,
                          budgetCost: 0,
                          actualCost: 0,
                        };
                      });

                      const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                      setLocalBudgetData({
                        ...enrichedBudgetData,
                        resourceBudget: {
                          ...enrichedBudgetData.resourceBudget,
                          byRole: updatedRoles,
                          byPerson: [...existingByPerson, ...newPersonBudgets],
                        },
                      });
                    }

                    toast.success(`Assigned ${selections.length} team member${selections.length !== 1 ? 's' : ''} to ${role}`);
                  }}
                />
              </>
            ) : (
              <div className="flex items-center justify-center min-h-[500px]">
                <Card className="w-full max-w-md border-dashed">
                  <CardContent className="flex flex-col items-center justify-center text-center p-12">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      <Clock className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                      Plan team capacity and track hours by creating a resource budget for this project.
                    </p>
                    <Button onClick={() => setEditResourceBudgetPageOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Resource Budget
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        )}

        {/* Cost Centres Tab */}
        <TabsContent value="cost-centres" className="space-y-4">
          {/* Header Section */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCostCentreColumnConfigOpen(true)}
                className="gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Configure Columns
              </Button>
              {!isStructureView && (
                <Button
                  size="sm"
                  onClick={() => setCreateCostCentrePanelOpen(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New
                </Button>
              )}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="relative px-3"
                      aria-label="Recycle Bin"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-medium bg-muted text-muted-foreground rounded-full border">
                        0
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Recycle Bin</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isStructureView && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfigureStructureOpen(true)}
                  className="gap-2"
                >
                  <Settings2 className="h-4 w-4" />
                  Configure Structure
                </Button>
              )}
              <div className="flex items-center gap-2">
                <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
                  Structure View
                </label>
                <Switch
                  id="structure-view-toggle"
                  checked={isStructureView}
                  onCheckedChange={setIsStructureView}
                />
              </div>
            </div>
          </div>

          {/* Content Display Area */}
          {isStructureView ? (
            // Structure View (Tree/Hierarchy)
            <CostCentresHierarchicalTable 
              data={hierarchicalCostCentres} 
              columns={costCentreColumns}
              useCardWrapper={false}
              onRowClick={(costCentre) => {
                setSelectedCostCentre(costCentre);
                setCostCentreDetailsPanelOpen(true);
              }}
            />
          ) : (
            // List/Table View
            <CostCentresTable 
              data={costCentres} 
              columns={costCentreColumns}
              useCardWrapper={false}
              onRowClick={(costCentre) => {
                setSelectedCostCentre(costCentre);
                setCostCentreDetailsPanelOpen(true);
              }}
            />
          )}
        </TabsContent>

        {/* Column Configurator Modal */}
        <CostCentresColumnConfigurator
          open={costCentreColumnConfigOpen}
          onOpenChange={setCostCentreColumnConfigOpen}
          columns={costCentreColumns}
          onSave={(newColumns) => {
            setCostCentreColumns(newColumns);
            // In a real app, save to user preferences/backend
          }}
        />

        {/* Create Cost Centre Panel */}
        <CreateCostCentrePanel
          open={createCostCentrePanelOpen}
          onOpenChange={setCreateCostCentrePanelOpen}
          existingCodes={[...costCentres.map(cc => cc.code), ...hierarchicalCostCentres.map(cc => cc.code)]}
          onSave={async (newCostCentre) => {
            const mode = getDataMode();
            
            if (mode === 'live' && projectId) {
              // Live mode - call the API to persist the cost centre
              try {
                const createdCostCentre = await createCostCentre({
                  projectId: numericProjectId,
                  code: newCostCentre.code,
                  name: newCostCentre.name,
                  type: newCostCentre.type,
                  status: newCostCentre.status,
                  billable: newCostCentre.billable,
                  owner: newCostCentre.owner,
                  description: newCostCentre.description,
                  costsBudget: newCostCentre.costsBudget,
                  revenueBudget: newCostCentre.revenueBudget,
                  hoursBudget: newCostCentre.hoursBudget,
                });
                
                // Use the ID from the API response
                const costCentreWithId: CostCentre = {
                  ...newCostCentre,
                  id: createdCostCentre.id || -Date.now(),
                };
                
                // Add to both flat and hierarchical lists
                setCostCentres(prev => [...prev, costCentreWithId]);
                setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
                
                console.log('Created new cost centre via API:', costCentreWithId);
              } catch (error) {
                console.error('Failed to create cost centre:', error);
                // Still add to local state for demo purposes, but log the error
                const id = -Date.now();
                const costCentreWithId: CostCentre = {
                  ...newCostCentre,
                  id,
                };
                setCostCentres(prev => [...prev, costCentreWithId]);
                setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
              }
            } else {
              // Demo mode - just update local state
              const id = -Date.now();
              const costCentreWithId: CostCentre = {
                ...newCostCentre,
                id,
              };
              
              // Add to both flat and hierarchical lists
              setCostCentres(prev => [...prev, costCentreWithId]);
              setHierarchicalCostCentres(prev => [...prev, { ...costCentreWithId, children: [] }]);
              
              console.log('Created new cost centre (demo mode):', costCentreWithId);
            }
          }}
        />

        {/* Purchases Tab */}
        <TabsContent value="purchases" className="space-y-4">
          <PurchasesCommitmentsTab projectId={numericProjectId} projectCode={project?.code} />
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          {/* Unfulfilled Roles Table */}
          {enrichedBudgetData?.resourceBudget?.byRole && project && (
            <UnfulfilledRolesTable 
              roles={enrichedBudgetData.resourceBudget.byRole}
              projectId={numericProjectId}
              projectStartDate={project.startDate}
              projectEndDate={project.endDate}
              availablePeople={employees}
              existingAllocations={resourceAllocations}
              onAssign={async (role, selections) => {
                // Handle assignment - update resource allocations and budget data
                const dataMode = getDataMode();
                
                // Calculate total hours being assigned
                const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);
                
                // In live mode, create resource allocations via API
                const createdAllocationIds: Map<number, string> = new Map();
                if (dataMode === 'live') {
                  try {
                    const now = new Date();
                    const startDate = project?.startDate || now.toISOString().split('T')[0];
                    const endDate = project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                    
                    // Create resource allocations for each selection
                    for (const selection of selections) {
                      const allocationData = {
                        projectId: numericProjectId,
                        employeeId: selection.personId,
                        role: role,
                        allocatedHours: selection.allocatedHours,
                        startDate: startDate,
                        endDate: endDate,
                        status: 'confirmed' as const,
                      };
                      
                      const createdAllocation = await createResourceAllocation(allocationData);
                      createdAllocationIds.set(selection.personId, createdAllocation.id);
                    }
                    // Also create PersonBudget records so Rate Card can show assigned people
                    let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                    if (!resourceBudgetId) {
                      console.warn('[onAssign/Team] resourceBudgetId not found in local state, fetching from API...');
                      resourceBudgetId = (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                    }
                    if (resourceBudgetId) {
                      const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(r => r.role === role);
                      for (const selection of selections) {
                        const employee = employees.find(e => e.id === selection.personId);
                        try {
                          await createPersonBudget({
                            resourceBudgetId,
                            personId: selection.personId,
                            personName: employee?.name || selection.personName,
                            roleId: matchedRole?.roleId,
                            budgetHours: selection.allocatedHours,
                            costRate: 0,
                          });
                        } catch (pbError) {
                          console.warn('Failed to create PersonBudget record:', pbError);
                        }
                      }
                    } else {
                      console.warn('[onAssign/Team] No resourceBudgetId available — skipping PersonBudget creation');
                    }
                  } catch (error) {
                    console.error('Failed to create resource allocations:', error);
                    toast.error('Failed to assign team members to project');
                    return;
                  }
                }
                
                // Update budget data to reflect the new assignments
                if (enrichedBudgetData?.resourceBudget?.byRole) {
                  const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map(r => {
                    if (r.role === role) {
                      return {
                        ...r,
                        allocatedHours: r.allocatedHours + totalAssignedHours,
                      };
                    }
                    return r;
                  });
                  
                  const newPersonBudgets = selections.map(selection => {
                    const employee = employees.find(e => e.id === selection.personId);
                    return {
                      id: -Date.now() - selection.personId,
                      personId: selection.personId,
                      personName: employee?.name || selection.personName,
                      role: role,
                      budgetHours: selection.allocatedHours,
                      actualHours: 0,
                      utilisation: 0,
                      costRate: 0,
                      budgetCost: 0,
                      actualCost: 0,
                    };
                  });

                  const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                  setLocalBudgetData({
                    ...enrichedBudgetData,
                    resourceBudget: {
                      ...enrichedBudgetData.resourceBudget,
                      byRole: updatedRoles,
                      byPerson: [...existingByPerson, ...newPersonBudgets],
                    },
                  });
                }
                
                // Update team members - either add new or update existing
                setTeamMembers(prev => {
                  const updatedMembers = [...prev];
                  const newMembers: TeamMember[] = [];
                  
                  selections.forEach(selection => {
                    // Check if team member already exists (by employeeId)
                    const existingIndex = updatedMembers.findIndex(
                      tm => tm.employeeId === selection.personId
                    );
                    
                    const employee = employees.find(e => e.id === selection.personId);
                    const now = new Date();
                    const startDate = project?.startDate || now.toISOString();
                    const endDate = project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
                    
                    if (existingIndex !== -1) {
                      // Team member already exists - update their allocated hours
                      const existing = updatedMembers[existingIndex];
                      const newAllocatedHours = existing.allocatedHours + selection.allocatedHours;
                      const remaining = newAllocatedHours - existing.actualHours;
                      const utilisationPercent = (existing.actualHours / newAllocatedHours) * 100;
                      
                      // Determine updated status
                      let status: TeamMember['status'];
                      if (utilisationPercent > 100) {
                        status = 'Over Allocated';
                      } else if (utilisationPercent > 85) {
                        status = 'At Risk';
                      } else {
                        status = 'On Track';
                      }
                      
                      // Update existing team member
                      updatedMembers[existingIndex] = {
                        ...existing,
                        allocatedHours: newAllocatedHours,
                        remaining,
                        utilisationPercent,
                        status,
                      };
                    } else {
                      // Team member doesn't exist - create new entry
                      // In live mode, actual hours start at 0; in demo mode, generate mock data
                      const actualHours = dataMode === 'live' ? 0 : Math.floor(selection.allocatedHours * (0.3 + Math.random() * 0.4));
                      const remaining = selection.allocatedHours - actualHours;
                      const utilisationPercent = selection.allocatedHours > 0 ? (actualHours / selection.allocatedHours) * 100 : 0;
                      
                      // Determine status
                      let status: TeamMember['status'];
                      if (utilisationPercent > 100) {
                        status = 'Over Allocated';
                      } else if (utilisationPercent > 85) {
                        status = 'At Risk';
                      } else {
                        status = 'On Track';
                      }
                      
                      // Generate avatar URL
                      const name = employee?.name || selection.personName;
                      const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;
                      
                      // Use the created allocation ID in live mode, or generate a temp ID in demo mode
                      const allocationId = createdAllocationIds.get(selection.personId) || `tm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                      
                      newMembers.push({
                        id: allocationId,
                        employeeId: selection.personId,
                        name: name,
                        email: employee?.email || '',
                        role: employee?.role || role, // Use employee's actual role, fallback to unfulfilled role
                        avatar: avatarUrl,
                        allocatedHours: selection.allocatedHours,
                        actualHours,
                        remaining,
                        startDate,
                        endDate,
                        status,
                        utilisationPercent,
                      });
                    }
                  });
                  
                  // Return updated members plus any new members
                  return [...updatedMembers, ...newMembers];
                });
                
                toast.success(
                  `Assigned ${selections.length} team member${selections.length !== 1 ? 's' : ''} to ${role} role`,
                  {
                    description: selections.map(s => `${s.personName}: ${s.allocatedHours}h`).join(', '),
                  }
                );
              }}
            />
          )}

          {/* Team Management Table */}
          <Card>
            <CardHeader>
              <CardTitle>Team Management</CardTitle>
              <CardDescription>Comprehensive team member tracking and allocation</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('name')}
                      >
                        Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('role')}
                      >
                        Role
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('allocatedHours')}
                      >
                        Allocated Hours
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('actualHours')}
                      >
                        Actual Hours
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-mr-3 h-8"
                        onClick={() => handleSort('remaining')}
                      >
                        Remaining
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTeamMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground h-32">
                        No team members found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTeamMembers.map(member => {
                      // TODO: Access Evo Integration - When integrating with Access Evo platform,
                      // attempt to fetch the user's Evo avatar from the platform API. If no avatar
                      // exists for the user, fall back to the EDS color scheme below with initials.
                      // Example: const evoAvatarUrl = await fetchEvoUserAvatar(member.platformUserId);
                      
                      // Generate deterministic color based on name for EDS styling (fallback when no Evo avatar)
                      const getAvatarColorClass = (name: string) => {
                        const colors = [
                          'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
                          'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300',
                          'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300',
                          'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
                          'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
                          'bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-300',
                          'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
                          'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
                        ];
                        const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                        return colors[hash % colors.length];
                      };

                      return (
                        <TableRow key={member.id}>
                          <TableCell>
                            <Avatar className="h-8 w-12 rounded-md">
                              <AvatarFallback className={`text-xs rounded-md ${getAvatarColorClass(member.name)}`}>
                                {getInitials(member.name)}
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>
                        <TableCell className="font-medium">
                          {member.name}
                        </TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell className="text-right font-sans">
                          {member.allocatedHours === 0 ? (
                            <span className="text-muted-foreground italic">Not allocated</span>
                          ) : (
                            `${member.allocatedHours.toFixed(0)}h`
                          )}
                        </TableCell>
                        <TableCell className="text-right font-sans">
                          {member.actualHours.toFixed(0)}h
                        </TableCell>
                        <TableCell className="text-right font-sans">
                          <span className={member.remaining < 0 ? 'text-error-600' : ''}>
                            {member.remaining.toFixed(0)}h
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(member.status, member.utilisationPercent)}>
                            {member.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {member.actualHours === 0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    onClick={() => handleRemoveTeamMember(member)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Remove from project</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Milestones Tab */}
        <TabsContent value="milestones" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Project Milestones</CardTitle>
              <CardDescription>Key deliverables and payment milestones</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Milestone</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Payment</TableHead>
                    <TableHead>Paid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {milestones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No milestones defined
                      </TableCell>
                    </TableRow>
                  ) : (
                    milestones.map(milestone => (
                      <TableRow key={milestone.id}>
                        <TableCell className="font-medium">{milestone.name}</TableCell>
                        <TableCell>{formatDate(milestone.dueDate)}</TableCell>
                        <TableCell>
                          {milestone.completedDate ? formatDate(milestone.completedDate) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              milestone.status === 'completed'
                                ? 'default'
                                : milestone.status === 'overdue'
                                ? 'destructive'
                                : 'outline'
                            }
                          >
                            {milestone.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {milestone.paymentAmount ? (
                            <SensitiveData>{formatCurrency(milestone.paymentAmount)}</SensitiveData>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {milestone.paymentReceived ? (
                            <CheckCircle className="h-4 w-4 text-success-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Tab */}
        <TabsContent value="time" className="space-y-4">
          <TimeEntriesTab 
            projectId={numericProjectId}
            projectCode={project?.code}
            timeEntries={timeEntries}
            employees={employees}
          />
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-approved-expenses"
                checked={includeApprovedExpenses}
                onCheckedChange={(checked) => setIncludeApprovedExpenses(checked === true)}
              />
              <label htmlFor="include-approved-expenses" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Approved Expenses
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-credit-card-expenses"
                checked={includeCreditCardExpenses}
                onCheckedChange={(checked) => setIncludeCreditCardExpenses(checked === true)}
              />
              <label htmlFor="include-credit-card-expenses" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Credit Card Expenses
              </label>
            </div>
            {expensesLoading && (
              <span className="text-sm text-muted-foreground">Loading...</span>
            )}
          </div>
          <ExpenseEntriesTab 
            projectId={numericProjectId}
            expenses={expenses}
            employees={employees}
          />
        </TabsContent>

        {/* Change Requests Tab */}
        <TabsContent value="changes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Change Requests</CardTitle>
              <CardDescription>Scope changes and their impact on budget and schedule</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Budget Impact</TableHead>
                    <TableHead className="text-right">Schedule Impact</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {changeRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No change requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    changeRequests.map(cr => (
                      <TableRow key={cr.id}>
                        <TableCell className="font-medium">{cr.title}</TableCell>
                        <TableCell>{cr.requestedBy}</TableCell>
                        <TableCell>{formatDate(cr.requestedDate)}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              cr.status === 'approved'
                                ? 'default'
                                : cr.status === 'rejected'
                                ? 'destructive'
                                : 'outline'
                            }
                          >
                            {cr.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <SensitiveData>{formatCurrency(cr.impactBudget)}</SensitiveData>
                        </TableCell>
                        <TableCell className="text-right">
                          {cr.impactSchedule > 0 ? `+${cr.impactSchedule}` : cr.impactSchedule} days
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
            </ScrollableTabsContainer>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Configure Structure Modal */}
      <ConfigureStructureModal
        open={configureStructureOpen}
        onOpenChange={setConfigureStructureOpen}
        projectName={project?.name || ''}
        costCentres={hierarchicalCostCentres}
        onSave={async (updatedStructure) => {
          // TODO: Implement API call to save structure
          console.log('Saving structure:', updatedStructure);
          setHierarchicalCostCentres(updatedStructure);
          // Show success toast
        }}
      />

      {/* Cost Centre Details Panel */}
      <CostCentreDetailsPanel
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        open={costCentreDetailsPanelOpen}
        onOpenChange={setCostCentreDetailsPanelOpen}
                onEdit={(updatedCostCentre) => {
                  // Update the cost centre in the state to refresh the table
                  setCostCentres(prev => prev.map(cc => 
                    cc.id === updatedCostCentre.id ? updatedCostCentre : cc
                  ));
                  setHierarchicalCostCentres(prev => prev.map(cc => 
                    cc.id === updatedCostCentre.id ? { ...cc, ...updatedCostCentre } : cc
                  ));
                  // Also update the selected cost centre if it's the same one
                  if (selectedCostCentre?.id === updatedCostCentre.id) {
                    setSelectedCostCentre(updatedCostCentre);
                  }
                }}
        onDelete={(costCentreId) => {
          // Delete the cost centre
          setCostCentres(prev => prev.filter(cc => cc.id !== costCentreId));
          setHierarchicalCostCentres(prev => prev.filter(cc => cc.id !== costCentreId));
          console.log('Deleted cost centre:', costCentreId);
          // In a real app, this would call an API to delete the cost centre
        }}
        onMarkInactive={(costCentreId) => {
          // Mark the cost centre as inactive
          console.log('Mark inactive cost centre:', costCentreId);
          // In a real app, this would call an API to update the cost centre status
        }}
        onExpandToFullView={() => {
          // Close the side drawer and open the full modal
          setCostCentreDetailsPanelOpen(false);
          setCostCentreFullModalOpen(true);
        }}
      />

      {/* Cost Centre Full Modal */}
      <CostCentreFullModal
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        allCostCentres={costCentres}
        open={costCentreFullModalOpen}
        onOpenChange={setCostCentreFullModalOpen}
        onCostCentreChange={(costCentre) => {
          setSelectedCostCentre(costCentre);
        }}
        onMinimize={() => {
          setCostCentreFullModalOpen(false);
          setCostCentreDetailsPanelOpen(true);
        }}
      />

      {/* Sticky Footer for Edit Mode */}
      {isEditMode && (
        <StickyFooter>
          <Button
            variant="outline"
            onClick={handleCancelEdit}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges}
          >
            Save Changes
          </Button>
        </StickyFooter>
      )}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Budget Edit Modals */}
      {budgetData && (
        <>
          <EditBudgetModal
            open={salesBudgetEditModalOpen}
            onOpenChange={setSalesBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || 'fixed-price'}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="sales"
          />

          <EditBudgetModal
            open={costBudgetEditModalOpen}
            onOpenChange={setCostBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || 'fixed-price'}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="cost"
          />

          <EditBudgetModal
            open={resourceBudgetEditModalOpen}
            onOpenChange={setResourceBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || 'fixed-price'}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="resource"
          />

          <BudgetHistoryModal
            open={budgetHistoryModalOpen}
            onOpenChange={setBudgetHistoryModalOpen}
            revisions={budgetData.revisions || []}
            projectName={project?.code ? `${project.code} - ${project.name}` : project?.name || budgetData.projectName || `Project ${projectId}`}
            onRestore={handleRestoreRevision}
          />

          <BudgetExportModal
            open={budgetExportModalOpen}
            onOpenChange={setBudgetExportModalOpen}
            budgetData={budgetData}
          />
        </>
      )}
    </div>
  );
}
